'use client';

import { type KeyboardEvent as ReactKeyboardEvent, type SyntheticEvent, useMemo } from 'react';

interface UseDisabledInteractionsPropsInterface {
  /**
   * Whether the element should remain keyboard focusable.
   *
   * When `true`, the element stays in the tab order (`tabIndex: 0`) so users
   * don't lose their place — useful for a `loading` state or genuinely
   * non-`<button>` interactive elements (e.g. `role="button"`).
   *
   * When `false`, the element is removed from the tab order (`tabIndex: -1`) —
   * useful for disabled anchors, which can't use the native `disabled`
   * attribute.
   *
   * @default false
   */
  focusable?: boolean;
}

const preventEvent = (event: SyntheticEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

// Only prevent keyboard events that would trigger a press. Navigation keys
// (Tab, arrows) are intentionally left alone so users can move focus away.
const preventKeyEvent = (event: ReactKeyboardEvent<HTMLElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    preventEvent(event);
  }
};

// Capture-phase events run top-down, so cancelling here stops both the slotted
// root and any Radix `Slottable`-composed child handlers before they fire.
// Cancelling in the capture phase also short-circuits the element's own
// bubble-phase handlers (e.g. a consumer-supplied `onClick`).
const POINTER_EVENTS = [
  'Click',
  'DoubleClick',
  'MouseDown',
  'MouseUp',
  'PointerDown',
  'PointerUp',
  'TouchStart',
  'TouchEnd',
  'TouchMove',
] as const;

const KEYBOARD_EVENTS = ['KeyDown', 'KeyUp', 'KeyPress'] as const;

const captureHandlers = {
  ...Object.fromEntries(POINTER_EVENTS.map((name) => [`on${name}Capture`, preventEvent])),
  ...Object.fromEntries(KEYBOARD_EVENTS.map((name) => [`on${name}Capture`, preventKeyEvent])),
};

/**
 * Hook that returns props to disable interactions on an element that can't (or
 * shouldn't) use the native `disabled` attribute — e.g. anchors, `role="button"`
 * elements, or a `loading` state where the element should stay focusable.
 *
 * It applies `aria-disabled`, a configurable `tabIndex` (via `focusable`), and
 * capture-phase event handlers that cancel every interaction. Because the
 * handlers run in the capture phase, they cancel the element's own events as
 * well as any child events composed onto it (e.g. through Radix `Slottable`).
 *
 * The returned props are always populated — the caller decides when to apply
 * them (spread them only when the element is disabled/loading). Note that props
 * returned should be spread after the component's rest props to prevent
 * accidental overrides.
 *
 * @param {object} props - Configuration object.
 * @param {boolean} [props.focusable=false] - Whether the element should remain keyboard focusable.
 * @returns {object} Object containing accessibility props and capture-phase event handlers.
 *
 * @see https://www.scottohara.me/blog/2021/05/28/disabled-links.html
 *
 * @example
 * // Loading button that stays focusable
 * const disabledInteractionsProps = useDisabledInteractions({ focusable: true });
 * return <button {...rest} {...(loading ? disabledInteractionsProps : {})} />;
 *
 * @example
 * // Disabled anchor removed from the tab order
 * const disabledInteractionsProps = useDisabledInteractions({ focusable: false });
 * return <a {...rest} {...(disabled ? disabledInteractionsProps : {})} />;
 */
export const useDisabledInteractions = ({
  focusable = false,
}: UseDisabledInteractionsPropsInterface = {}) =>
  useMemo(
    () => ({
      'aria-disabled': true,
      'tabIndex': focusable ? 0 : -1,
      ...captureHandlers,
    }),
    [focusable],
  );
